import React, { useEffect, useState } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import { ViewConfig } from "../types/views";
import { useApi as useSimpleApi } from "../hooks/useSimpleApi";
import { useAuth } from "../contexts/AuthContext";
import DynamicView from "./DynamicView";

interface ViewRouterProps {
  isKioskMode?: boolean;
}

/**
 * ViewRouter - Vereinfachter Router für Views
 *
 * Lädt Views vom Backend und rendert die passende View basierend auf der Route.
 * Auth-Logik ist jetzt im AuthContext zentralisiert.
 */
const ViewRouter: React.FC<ViewRouterProps> = ({ isKioskMode = false }) => {
  const [views, setViews] = useState<ViewConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayViewMapping, setDisplayViewMapping] = useState<
    Record<string, string>
  >({});

  const location = useLocation();
  const params = useParams<{ displayId: string }>();
  const api = useSimpleApi();
  const { mode, isAuthenticated, isAdmin, canAccessView, user } = useAuth();

  // DisplayId aus URL-Parametern extrahieren (für Kiosk-Mode)
  const displayId = isKioskMode ? params.displayId : undefined;

  // Views vom Backend laden
  useEffect(() => {
    const loadViews = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get auth headers if needed
        const headers: any = {};
        if (mode === "closed" && isAuthenticated) {
          const token = localStorage.getItem("customer_token");
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        }

        // Load views
        const response = await api.get("/api/views/", { headers });
        const viewsData = response.data;

        if (Array.isArray(viewsData)) {
          setViews(viewsData);
          console.log(
            `🔄 ViewRouter loaded ${viewsData.length} views`,
            isAdmin ? "(Admin: all views)" : "(User: filtered views)",
          );
        } else {
          throw new Error("Invalid views data format");
        }

        // Für Kiosk-Mode: Lade Display-Mapping
        if (isKioskMode && displayId) {
          try {
            const displayResponse = await api.get(
              `/api/views/display/${displayId}/config`,
              { headers },
            );
            const displayConfig = displayResponse.data;

            if (displayConfig?.default_view) {
              setDisplayViewMapping((prev) => ({
                ...prev,
                [displayId]: displayConfig.default_view,
              }));
              console.log(
                `🖥️ Display ${displayId} mapped to view '${displayConfig.default_view}'`,
              );
            }
          } catch (displayErr: any) {
            console.warn(
              `⚠️ Could not load display config for ${displayId}:`,
              displayErr.message,
            );
            // Fallback mapping
            setDisplayViewMapping((prev) => ({
              ...prev,
              [displayId]: "default",
            }));
          }
        }
      } catch (err: any) {
        console.error("❌ ViewRouter failed to load views:", err);
        setError(err.message || "Failed to load views");

        // Fallback view for error cases
        setViews([
          {
            id: 1,
            view_id: "default",
            name: "Standard",
            description: "Fallback view",
            route: "/",
            data_source: "mte_position_items_joined",
            filters: { monitor_ansicht: "M-Fert" },
            sort_rules: [],
            columns: [],
            refresh_interval: 30000,
            kiosk_settings: {
              enabled: true,
              refresh_interval: 30000,
              large_fonts: false,
            },
            created_at: "",
            updated_at: "",
            created_by: "system",
            is_active: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadViews();
  }, [api, isKioskMode, displayId, mode, isAuthenticated, isAdmin]);

  // Route zu View-ID mapping
  const findViewByRoute = (pathname: string): ViewConfig | null => {
    // Handle display-specific routes (/display/HDMI-A-1)
    if (isKioskMode && displayId) {
      console.log(`🖥️ Kiosk mode: resolving display ID '${displayId}' to view`);

      // Use dynamic display mapping from backend
      const targetViewId = displayViewMapping[displayId];
      if (targetViewId) {
        const view = views.find((v) => v.view_id === targetViewId);
        if (view) {
          console.log(
            `✅ Display ${displayId} mapped to view '${targetViewId}'`,
          );
          return view;
        } else {
          console.warn(
            `⚠️ View '${targetViewId}' not found for display ${displayId}`,
          );
        }
      } else {
        console.warn(`⚠️ No view mapping found for display ${displayId}`);
      }

      // Fallback to default view for unknown displays
      const defaultView = views.find((v) => v.view_id === "default");
      if (defaultView) {
        console.log(`🔄 Fallback to default view for display ${displayId}`);
        return defaultView;
      }
    }

    // Exakte Route-Übereinstimmung
    const exactMatch = views.find((view) => view.route === pathname);
    if (exactMatch) {
      return exactMatch;
    }

    // Fallback: Versuche view_id als Route zu interpretieren
    const pathWithoutSlash = pathname.replace("/", "");
    const idMatch = views.find((view) => view.view_id === pathWithoutSlash);
    if (idMatch) {
      return idMatch;
    }

    return null;
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Views werden geladen...</p>
        </div>
      </div>
    );
  }

  // Error State mit Fallback
  if (error && views.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Views konnten nicht geladen werden
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Neu laden
          </button>
        </div>
      </div>
    );
  }

  // Route matching
  const currentView = findViewByRoute(location.pathname);

  // Check access permissions (nur wenn nicht im Kiosk-Mode)
  if (!isKioskMode && currentView) {
    // Im geschlossenen Modus: Prüfe Berechtigung
    if (mode === "closed" && !canAccessView(currentView.view_id)) {
      // Redirect to user's default view
      const userView = user?.view_id
        ? views.find((v) => v.view_id === user.view_id)
        : null;

      if (userView) {
        console.log(
          `🔐 Access denied to view ${currentView.view_id}, redirecting to ${userView.route}`,
        );
        return <Navigate to={userView.route} replace />;
      } else {
        // No access to any view
        return (
          <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="text-center max-w-md">
              <div className="text-red-500 text-6xl mb-4">🚫</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Kein Zugriff
              </h2>
              <p className="text-gray-600">
                Sie haben keinen Zugriff auf diese Ansicht.
              </p>
            </div>
          </div>
        );
      }
    }
  }

  // 404 - View nicht gefunden
  if (!currentView) {
    console.warn(
      `⚠️ ViewRouter: No view found for route '${location.pathname}'`,
    );
    console.log(
      "📋 Available routes:",
      views.map((v) => v.route),
    );

    // Redirect zur default View oder erste verfügbare View
    const defaultView = views.find((v) => v.view_id === "default") || views[0];
    if (defaultView) {
      console.log(`🔄 Redirecting to default view: ${defaultView.route}`);
      return <Navigate to={defaultView.route} replace />;
    }

    // Fallback 404
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="text-gray-400 text-6xl mb-4">🔍</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Ansicht nicht gefunden
          </h2>
          <p className="text-gray-600 mb-4">
            Die Route{" "}
            <code className="bg-gray-100 px-2 py-1 rounded">
              {location.pathname}
            </code>{" "}
            ist nicht verfügbar.
          </p>
          {(mode === "open" || isAdmin) && (
            <div className="text-sm text-gray-500 mb-4">
              Verfügbare Ansichten:
              <ul className="mt-2 space-y-1">
                {views.map((view) => (
                  <li key={view.view_id}>
                    <a
                      href={view.route}
                      className="text-blue-600 hover:underline"
                    >
                      {view.route} - {view.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Success: Render DynamicView
  console.log(
    `✅ ViewRouter rendering view '${currentView.view_id}' for route '${location.pathname}'`,
  );

  return (
    <DynamicView
      viewId={currentView.view_id}
      isKioskMode={isKioskMode}
      displayId={displayId}
    />
  );
};

export default ViewRouter;
